import React, { useState } from 'react';
import { modulesAPI } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DISCLAIMER_FINANCIERO = `⚠️ AVISO IMPORTANTE:
Esta herramienta entrega estimaciones referenciales con fines informativos y educativos.
No constituye asesoría contable, tributaria ni financiera profesional.
No reemplaza la revisión de un contador certificado.`;

const Accounting = () => {
  const [requestType, setRequestType] = useState('financial_simulation');
  const [formData, setFormData] = useState({
    ingresos: '',
    gastos: '',
    periodo: '',
    descripcion: '',
    query: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const requestTypes = [
    { 
      id: 'financial_simulation', 
      name: '📊 Simulación Financiera',
      description: 'Proyección de ingresos, gastos y utilidades (estimación)'
    },
    { 
      id: 'tax_simulation', 
      name: '🧮 Simulación Tributaria',
      description: 'Estimación de impuestos (NO oficial)'
    },
    { 
      id: 'analyze_expenses', 
      name: '📈 Análisis de Gastos',
      description: 'Análisis informativo de gastos'
    },
    { 
      id: 'financial_education', 
      name: '📚 Educación Financiera',
      description: 'Explicación de conceptos financieros'
    },
    { 
      id: 'financial_report', 
      name: '📋 Modelo de Reporte',
      description: 'Plantilla informativa (no oficial)'
    }
  ];

  const handleProcess = async () => {
    setLoading(true);
    setResult(null);

    try {
      let data = {};
      
      if (requestType === 'financial_simulation' || requestType === 'tax_simulation') {
        data = {
          ingresos: formData.ingresos,
          gastos: formData.gastos,
          periodo: formData.periodo,
          descripcion: formData.descripcion
        };
      } else if (requestType === 'analyze_expenses') {
        data = {
          expenses: formData.gastos,
          period: formData.periodo
        };
      } else if (requestType === 'financial_education') {
        data = {
          query: formData.query
        };
      } else if (requestType === 'financial_report') {
        data = {
          periodo: formData.periodo,
          ingresos: formData.ingresos,
          gastos: formData.gastos
        };
      }

      const response = await modulesAPI.accountingProcess({ 
        request_type: requestType, 
        data 
      });
      setResult(response.data);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      setResult({ 
        success: false, 
        error: typeof errorDetail === 'object' ? errorDetail.message : errorDetail || 'Error al procesar solicitud',
        disclaimer: DISCLAIMER_FINANCIERO
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-8 max-w-4xl mx-auto">
          {/* Header con nuevo nombre */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              🧮 Simulador Financiero y Tributario
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Herramienta de simulación con fines informativos y educativos.
            </p>
          </div>

          {/* Disclaimer permanente */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-bold text-amber-800 dark:text-amber-300 mb-1">
                  Aviso Importante
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Esta herramienta entrega <strong>estimaciones referenciales</strong> con fines informativos.
                  No constituye asesoría contable ni reemplaza la revisión de un contador certificado.
                </p>
              </div>
            </div>
          </div>

          {/* Selector de tipo */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-sm mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Tipo de Simulación
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {requestTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setRequestType(type.id)}
                  className={`p-4 rounded-lg text-left transition-all ${
                    requestType === type.id
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="font-medium">{type.name}</div>
                  <div className={`text-xs mt-1 ${requestType === type.id ? 'text-blue-100' : 'text-gray-500'}`}>
                    {type.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Formulario dinámico */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-sm mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Datos para Simulación
            </h2>
            
            {(requestType === 'financial_simulation' || requestType === 'tax_simulation' || requestType === 'financial_report') && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ingresos Estimados (CLP)
                  </label>
                  <input
                    type="text"
                    value={formData.ingresos}
                    onChange={(e) => handleInputChange('ingresos', e.target.value)}
                    placeholder="Ej: 2.500.000"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gastos Estimados (CLP)
                  </label>
                  <input
                    type="text"
                    value={formData.gastos}
                    onChange={(e) => handleInputChange('gastos', e.target.value)}
                    placeholder="Ej: 800.000"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Período
                  </label>
                  <input
                    type="text"
                    value={formData.periodo}
                    onChange={(e) => handleInputChange('periodo', e.target.value)}
                    placeholder="Ej: Enero 2025"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descripción Adicional (opcional)
                  </label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    placeholder="Describe tu situación financiera..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            {requestType === 'analyze_expenses' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Detalle de Gastos
                  </label>
                  <textarea
                    value={formData.gastos}
                    onChange={(e) => handleInputChange('gastos', e.target.value)}
                    placeholder="Describe tus gastos: Arriendo $500.000, Servicios $80.000, etc."
                    rows={4}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Período
                  </label>
                  <input
                    type="text"
                    value={formData.periodo}
                    onChange={(e) => handleInputChange('periodo', e.target.value)}
                    placeholder="Ej: Diciembre 2024"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            {requestType === 'financial_education' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ¿Qué concepto quieres aprender?
                  </label>
                  <textarea
                    value={formData.query}
                    onChange={(e) => handleInputChange('query', e.target.value)}
                    placeholder="Ej: ¿Qué es el IVA y cómo se calcula? ¿Cómo funciona el impuesto a la renta?"
                    rows={3}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleProcess}
              disabled={loading}
              className="mt-6 w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '⏳ Procesando...' : '📊 Generar Simulación'}
            </button>
          </div>

          {/* Resultado */}
          {result && (
            <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {result.success ? '📋 Resultado' : '❌ Error'}
                </h2>
                {result.result_type && (
                  <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-bold rounded-full">
                    {result.result_type}
                  </span>
                )}
              </div>

              {result.success ? (
                <div className="space-y-4">
                  {/* Etiqueta de simulación */}
                  {result.label && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-blue-700 dark:text-blue-300 text-sm font-medium">
                      {result.label}
                    </div>
                  )}

                  {/* Contenido */}
                  <div className="prose dark:prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap text-sm bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto">
                      {result.simulation || result.analysis || result.content || result.report}
                    </pre>
                  </div>

                  {/* Warning adicional */}
                  {result.warning && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-orange-700 dark:text-orange-300 text-sm">
                      {result.warning}
                    </div>
                  )}

                  {/* Disclaimer del resultado */}
                  <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {result.disclaimer}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-red-600 dark:text-red-400">
                  <p>{result.error}</p>
                  {result.disclaimer && (
                    <p className="mt-4 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {result.disclaimer}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info de limitaciones */}
          <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-3">
              ℹ️ Sobre este Simulador
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-green-600 dark:text-green-400 mb-2">✅ Qué SÍ puedes hacer:</h4>
                <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Simular ingresos y gastos</li>
                  <li>• Estimar impuestos (referencial)</li>
                  <li>• Aprender conceptos financieros</li>
                  <li>• Proyectar escenarios hipotéticos</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">❌ Qué NO hace:</h4>
                <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Emitir facturas oficiales</li>
                  <li>• Generar boletas de honorarios</li>
                  <li>• Declaraciones tributarias</li>
                  <li>• Reemplazar a un contador</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default Accounting;
